import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { QA_STATUS_OPTIONS } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import { useMutationData } from "@hooks/useMutationData";
import { useSearchInfo } from "@hooks/useSearchInfo";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { useTheme } from "@hooks/useTheme";
import { formatDate, formatDateTime, uppercase } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { getIndexDBData, STORES, deleteDataInIndexedDb } from "@utils/indexedDb";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { useDataSyncRefresh } from "@hooks/useDataSyncRefresh";

export default function QAList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasDeletePermission } = useRoutePermissions();
  const { colors } = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [searchFilters, setSearchFilters] = useState({});
  const [qaList, setQaList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.QA_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isQAListLoading, setIsQAListLoading] = useState(false);
  const isInitialMount = useRef(true);
  const prevSearchFilters = useRef({});
  const prevCurrentPage = useRef(1);

  const { creatorList, fetchSearchInfo } = useSearchInfo();


  useEffect(() => {
    fetchData();
    fetchSearchInfo();
  }, []);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  useEffect(() => {
    if (!isInitialMount.current) {
      const currentFilters = JSON.stringify(searchFilters);
      const prevFilters = JSON.stringify(prevSearchFilters.current);

      if (currentFilters !== prevFilters) {
        if (currentPage !== 1) {
          setCurrentPage(1);
        } else {
          fetchData();
        }
        prevSearchFilters.current = searchFilters;
      }
    } else {
      isInitialMount.current = false;
      prevSearchFilters.current = searchFilters;
      prevCurrentPage.current = currentPage;
    }
  }, [searchFilters]);

  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);


  const columns = ["qnaSeq", "title", "qnaStatusCdName", "viewCnt", "createrName", "createdAt"];

  const columnConfig = {
    qnaSeq: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("qa.qa-sequence"),
    },
    title: {
      style: {
        width: "40%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("qa.title"),
    },
    qnaStatusCdName: {
      style: {
        width: "15%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("qa.qa-status"),
      customRender: (value) => {
        const statusMap = {
          "Question": t("qa.question"),
          "Answer": t("qa.answer"),
        };
        return statusMap[value] || value;
      },
    },
    viewCnt: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("qa.view-count"),
    },
    createrName: {
      style: {
        width: "15%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("qa.creator"),
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      style: {
        width: "12%",
        whiteSpace: "normal",
        textAlign: "left",
        overflowWrap: "initial",
      },
      headerRender: () => t("qa.creation-date"),
      customRender: (value) => formatDateTime(value),
    },
  };

  const searchFields = [
    {
      name: "title",
      label: t("qa.title"),
      placeholder: t("qa.title-placeholder"),
    },
    {
      name: "qnaStatusCd",
      label: t("qa.qa-status"),
      type: "select",
      keepRawLabel: true,
      placeholder: t("qa.qa-status-options-placeholder"),
      options: [...QA_STATUS_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))],
    },
    {
      name: "question",
      label: t("qa.question"),
      placeholder: t("qa.question-placeholder"),
    },
    {
      name: "createrId",
      label: t("qa.creator"),
      type: "select",
      keepRawLabel: true,
      placeholder: t("qa.creator-placeholder"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "openFlag",
      label: t("qa.disclose"),
      type: "select",
      keepRawLabel: true,
      placeholder: t("qa.disclose-options-placeholder"),
      options: [
        { value: "Y", label: t("common.yes") },
        { value: "N", label: t("common.no") },
      ],
    },
    {
      name: "createdAtFrom",
      label: t("common.create-at-from"),
      placeholder: t("common.create-at-from"),
      type: "date",
    },
    {
      name: "createdAtTo",
      label: t("common.create-at-to"),
      placeholder: t("common.create-at-to"),
      type: "date",
    },
    {
      name: "answeredAtFrom",
      label: t("qa.answered-at-from"),
      placeholder: t("qa.answered-at-from"),
      type: "date",
    },
    {
      name: "answeredAtTo",
      label: t("qa.answered-at-to"),
      placeholder: t("qa.answered-at-to"),
      type: "date",
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "search",
    setSearchCriteria: setSearchFilters,
  });

  useEffect(() => {
    fetchData();
  }, [showAdvancedSearch]);

  const handleDeleteQA = () => {
    setShowDeleteModal(true);
  };

  const deleteData = useMutationData(
    queryKeys.deleteQA,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        await deleteDataInIndexedDb(STORES.QNAS, selectedRows);
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        showSuccessToast(t("qa.delete-qa-successfully", { count: selectedRows.length }));
        setSelectedRows([]);
        queryClient.invalidateQueries(queryKeys.getQAList);
      },
      onError: (error) => {
        console.error("Error during delete data:", error);
        showErrorToast(t("qa.delete-qa-failed"));
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const bodyRequest = selectedRows;
      await deleteData.mutateAsync(bodyRequest);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting Q&A:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddQA = () => {
    navigate("/question-and-answer/new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.qnaSeq}`);
  };

  const fetchData = () => {
    const effectiveCriteria = resolveSearchCriteria(searchFilters, {
      quickSearchField: "search",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      const value = effectiveCriteria[key];
      if (value !== "" && value !== undefined) {
        // Format date fields using formatDate function
        if (key === "createdAtFrom" || key === "createdAtTo" || key === "answeredAtFrom" || key === "answeredAtTo") {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest["pageSize"] = itemsPerPage;
    bodyRequest["startRow"] = itemsPerPage * (currentPage - 1);
    getListData(bodyRequest);
  };

  // Auto-refresh data when sync completes
  useDataSyncRefresh(fetchData);

  const getListData = async (bodyRequest = {}) => {
    setIsQAListLoading(true);
    try {
      const { pageSize = itemsPerPage, startRow = 0, ...filters } = bodyRequest;
      const response = await getIndexDBData(STORES.QNAS, {
        pageSize,
        startRow,
        filters,
        sortKey: "qnaSeq",
      });

      if (response && typeof response !== "string") {
        setQaList(response.object || []);
        const totalCount = response.objectCount || 0;
        setTotalData(totalCount);
        setTotalPages(Math.ceil(totalCount / (pageSize || 1)));
      }
    } catch (error) {
      console.error("Error fetching QAs from IndexedDB:", error);
      showErrorToast(t("qa.get-qa-list-error"));
    } finally {
      setIsQAListLoading(false);
    }
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters(prev => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="qa-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`} data-testid="qa-list-header">
        <div className="flex items-center justify-between">
          <h1
            className={`text-[28px] font-bold ${colors.textPrimary} uppercase`}
            data-testid="qa-list-title"
          >
            {uppercase(t("qa.qa-list"))}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 ${colors.bgSecondary}`} data-testid="qa-list-controls">
        <TableHeaderControls
          controlsTestId="qa-list-table-header-controls"
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          isAdvancedPanelOpen={showAdvancedSearch}
          onDeleteProject={handleDeleteQA}
          onAdd={handleAddQA}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="search"
          searchValue={searchFilters.search ?? ""}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("qa.add-qa")}
          titleDeleteButton={t("qa.delete-qa")}
          isDisplayDeleteButton={hasDeletePermission}
          searchInputTestId="qa-list-search-input"
          addButtonTestId="qa-list-add-button"
          deleteButtonTestId="qa-list-delete-button"
          advancedSearchButtonTestId="qa-list-advanced-search-button"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="qa-list-advanced-search">
          <AdvancedSearch
            data-testid="qa-list-advanced-search-panel"
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Q&A Table */}
      <div className="flex-grow min-h-0" data-testid="qa-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={qaList}
          columns={columns}
          onRowClick={onRowClick}
          loading={isQAListLoading}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="qnaSeq"
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="qa-list-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("qa.delete-qa"))}
        description={t("qa.delete-qa-warning", { count: selectedRows.length })}
        loading={deleteLoading}
      />
    </div>
  );
}
